// src/pages/About.jsx
import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getAboutContent } from '../config/api';

export default function About() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [triptych, setTriptych] = useState([null, null, null]);
  const [stylePhotos, setStylePhotos] = useState([]);
  const [bioHtml, setBioHtml] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    getAboutContent().then(res => {
      if (!res.data) return;
      const { bio_html, triptych: tri, style_photos } = res.data;
      setBioHtml(bio_html || '');
      setTriptych([1, 2, 3].map(pos => {
        const found = tri.find(t => t.position === pos);
        return found?.url || null;
      }));
      setStylePhotos(style_photos || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!lightboxOpen) {
      setExpandedIndex(null);
      return;
    }
    // iOS Safari ignores overflow:hidden on body — use position:fixed to truly lock scroll
    const y = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${y}px`;
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, y);
    };
  }, [lightboxOpen]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (expandedIndex !== null) setExpandedIndex(null);
        else setLightboxOpen(false);
        return;
      }
      if (expandedIndex !== null) {
        if (e.key === 'ArrowRight')
          setExpandedIndex(i => (i + 1) % stylePhotos.length);
        if (e.key === 'ArrowLeft')
          setExpandedIndex(i => (i - 1 + stylePhotos.length) % stylePhotos.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, expandedIndex, stylePhotos.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 50) return;
    if (delta < 0)
      setExpandedIndex(i => (i + 1) % stylePhotos.length); // swipe left → next
    else
      setExpandedIndex(i => (i - 1 + stylePhotos.length) % stylePhotos.length); // swipe right → prev
  };

  return (
    <div style={{ background: 'var(--color-bg)', minHeight: '100vh' }}>
      <Navbar />

      {/* ── Triptych ── */}
      <div data-testid="triptych" className="triptych-grid"
        style={{ padding: '24px 24px 0', paddingTop: 'calc(24px + 64px)' }}>
        {triptych.map((url, i) => (
          <div key={i} style={{ aspectRatio: '3/4', overflow: 'hidden' }}>
            {url
              ? <img src={url} alt={`Bhagyashree — portrait ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <div data-testid={`triptych-placeholder-${i}`} style={{ width: '100%', height: '100%', background: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            }
          </div>
        ))}
      </div>

      {/* ── Bio ── */}
      <div data-testid="bio-section"
        style={{ padding: '56px 52px 48px', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {bioHtml ? (
          <div dangerouslySetInnerHTML={{ __html: bioHtml }}
            style={{ maxWidth: 560, color: 'rgba(255,255,255,0.7)', fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.9, textAlign: 'left' }} />
        ) : (
          <>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.22em', marginBottom: 16, display: 'block' }}>the photographer</span>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 48, fontWeight: 300, color: '#fff', lineHeight: 1.05, marginBottom: 4 }}>Hi, I'm <em>Bhagyashree</em></h1>
            <div style={{ width: 36, height: 1, background: 'var(--color-accent)', margin: '20px auto', opacity: 0.5 }} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 20, display: 'block' }}>Photographer · Pune, Maharashtra</span>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 21, fontWeight: 300, fontStyle: 'italic', color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, marginBottom: 20, maxWidth: 560 }}>"I'm a simple girl looking for magic in everyday moments — celebrating everything and everyone, imperfect and perfect."</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 2, color: 'rgba(255,255,255,0.5)', marginBottom: 12, maxWidth: 540 }}>I believe in capturing the unposed, authentic moments that tell your unique story. From the anticipation of maternity to the tiny details of newborn life, my lens seeks the real, raw beauty in every frame.</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, lineHeight: 2, color: 'rgba(255,255,255,0.5)', maxWidth: 540 }}>Based in Pune, I work with families who value natural, emotion-driven photography over perfect poses. Every session is a collaboration, and every photo is a piece of your story.</p>
          </>
        )}
      </div>

      {/* ── CTA ── */}
      <div style={{ padding: '44px 52px 56px', textAlign: 'center' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300, fontStyle: 'italic', color: 'rgba(255,255,255,0.3)', display: 'block', marginBottom: 20 }}>Curious about how I work?</span>
        <button data-testid="style-btn" onClick={() => setLightboxOpen(true)}
          style={{ display: 'inline-block', background: 'transparent', border: '1px solid rgba(212,168,67,0.55)', color: 'var(--color-accent)', padding: '13px 34px', fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', fontFamily: 'var(--font-body)', cursor: 'pointer' }}>
          About My Style →
        </button>
      </div>

      {/* ── Lightbox ── */}
      {lightboxOpen && (
        <div data-testid="lightbox" role="dialog" aria-modal="true" aria-label="My Photography Style"
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 200, overflowY: 'auto', padding: '40px 32px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
              {expandedIndex !== null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    data-testid="back-to-grid"
                    onClick={() => setExpandedIndex(null)}
                    style={{ background: 'transparent', border: 'none', color: 'var(--color-accent)', fontSize: 12, letterSpacing: '0.05em', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0 }}
                  >
                    ‹ grid
                  </button>
                  <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300, fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>
                    My Photography Style
                  </span>
                </div>
              ) : (
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 300, fontStyle: 'italic', color: '#fff', margin: 0 }}>
                  My Photography Style
                </h2>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {expandedIndex !== null && (
                  <span
                    data-testid="photo-counter"
                    style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'var(--font-body)' }}
                  >
                    {expandedIndex + 1} / {stylePhotos.length}
                  </span>
                )}
                <button
                  data-testid="lightbox-close"
                  onClick={() => setLightboxOpen(false)}
                  aria-label="Close"
                  style={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', fontSize: 18, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  ✕
                </button>
              </div>
            </div>
            {expandedIndex !== null ? (
              /* ── State 2: expanded single photo ── */
              <div
                data-testid="expanded-photo"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <div style={{ position: 'relative', width: '100%', maxWidth: 'calc((100vh - 100px) * 0.75)', maxHeight: 'calc(100vh - 100px)', aspectRatio: '3/4', margin: '0 auto' }}>
                  <img
                    src={stylePhotos[expandedIndex].url}
                    alt={`Style ${expandedIndex + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <button
                    data-testid="prev-photo"
                    aria-label="Previous photo"
                    onClick={() => setExpandedIndex(i => (i - 1 + stylePhotos.length) % stylePhotos.length)}
                    style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 48, background: 'linear-gradient(to right, rgba(0,0,0,0.55), transparent)', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    ‹
                  </button>
                  <button
                    data-testid="next-photo"
                    aria-label="Next photo"
                    onClick={() => setExpandedIndex(i => (i + 1) % stylePhotos.length)}
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 48, background: 'linear-gradient(to left, rgba(0,0,0,0.55), transparent)', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    ›
                  </button>
                </div>
              </div>
            ) : (
              /* ── State 1: grid + style statement ── */
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 40 }}>
                  {stylePhotos.length > 0
                    ? stylePhotos.map((photo, i) => (
                        <div
                          key={photo.id}
                          data-testid={`style-photo-${i}`}
                          onClick={() => setExpandedIndex(i)}
                          style={{ aspectRatio: '3/4', overflow: 'hidden', cursor: 'pointer' }}
                        >
                          <img
                            src={photo.url}
                            alt={`Style ${i + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </div>
                      ))
                    : Array.from({ length: 6 }, (_, i) => (
                        <div
                          key={i}
                          data-testid={`style-placeholder-${i}`}
                          style={{ aspectRatio: '3/4', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                            Style {i + 1}
                          </span>
                        </div>
                      ))
                  }
                </div>
                <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center', padding: '36px 24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--color-accent)', textTransform: 'uppercase', letterSpacing: '0.2em', display: 'block', marginBottom: 16 }}>My approach</span>
                  <p style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 300, fontStyle: 'italic', color: 'rgba(255,255,255,0.88)', lineHeight: 1.55, marginBottom: 16 }}>"Natural light. Genuine emotion. Authentic moments."</p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.9, color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>I don't believe in forced poses or artificial setups. I capture real connections — the quiet glances, the spontaneous laughter, the tender touches that make your story uniquely yours.</p>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.9, color: 'rgba(255,255,255,0.55)' }}>Whether it's the glow of pregnancy, the wonder of a newborn's first days, or the beautiful chaos of family life, I'm there to document it as it unfolds — naturally and authentically.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
